import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { listItemUoms } from '../items/api';
import { postMove, scanItem, scanLocation, type ScannedItemDto, type ScannedLocationDto } from './api';

/**
 * Moving stock from one place to another.
 *
 * A SCREEN, not a dialog, and reached straight from the menu — because that is how the job is done.
 * An operator stands in an aisle and moves one pallet after another; the old dialog made you find
 * the item on the inquiry screen first, which adds two steps to every single move. Legacy's
 * ItemMove screen is a form you sit in and repeat, with Save leaving you ready for the next one.
 *
 * FROM AND TO TAKE EITHER A LOCATION OR A LICENSE PLATE, exactly as legacy's two boxes do
 * (ItemMoveController.CheckLocation): it looks for a location name first and falls back to a plate.
 * When it is a plate, the location the plate is standing in is what gets posted — the operator
 * never has to know where that is. Validated as soon as the field is left, so a mis-scan costs one
 * beep rather than a failed post at the end.
 *
 * NO STOCK CATEGORY. An earlier version of this screen had a bucket picker with a "change bucket"
 * toggle, which was invented — legacy has no such thing anywhere in the move path. A location
 * carries its own category, so moving a pallet onto a QC rack is what puts the stock in QC. Tenant
 * 049 reads it from each location and ignores anything sent.
 */
export function MoveRoute() {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as { itemNumber?: string };
  const canMove = usePermissions().has('inventory:adjust');

  const [fromText, setFromText] = useState('');
  const [toText, setToText] = useState('');
  const [from, setFrom] = useState<ScannedLocationDto | null>(null);
  const [to, setTo] = useState<ScannedLocationDto | null>(null);
  const [fromError, setFromError] = useState<string | null>(null);
  const [toError, setToError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<'from' | 'to' | 'item' | null>(null);

  const [itemText, setItemText] = useState('');
  const [item, setItem] = useState<ScannedItemDto | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);

  const [quantity, setQuantity] = useState<number | ''>('');
  const [uomId, setUomId] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fromRef = useRef<HTMLInputElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const itemRef = useRef<HTMLInputElement>(null);
  const qtyRef = useRef<HTMLInputElement>(null);

  const uoms = useQuery({
    queryKey: ['item-uoms', item?.itemId ?? 'none'] as const,
    queryFn: ({ signal }) => listItemUoms(item!.itemId, signal),
    enabled: item !== null,
  });

  const uomOptions = useMemo(
    () => (uoms.data ?? []).map((u) => ({ value: u.uomId, label: u.uomCode })),
    [uoms.data],
  );

  /**
   * Resolves one of the two boxes. Shared, because From and To differ only in what they mean:
   * either may be a location or a plate, and legacy validates both the same way.
   */
  const resolveEnd = useCallback(
    async (which: 'from' | 'to', raw?: string, advanceTo?: HTMLInputElement | null) => {
      // The value comes from the EVENT, not from state. A scanner types the whole code and sends
      // Enter or Tab in the same tick, so a handler reading state would read what was there before
      // the keystroke — an empty string on the first scan — and quietly do nothing.
      const text = raw ?? (which === 'from' ? fromText : toText);
      const setValue = which === 'from' ? setFrom : setTo;
      const setText = which === 'from' ? setFromText : setToText;
      const setError = which === 'from' ? setFromError : setToError;
      const current = which === 'from' ? from : to;

      const code = text.trim();
      if (!code) {
        setValue(null);
        setError(null);
        return;
      }
      // Already resolved to this exact thing — do not ask again on every blur.
      if (current && (current.licensePlateNumber ?? current.locationCode) === code.toUpperCase()) {
        advanceTo?.focus();
        return;
      }

      setResolving(which);
      setError(null);
      try {
        const found = await scanLocation(code);
        setValue(found);
        // Echo back what it turned out to be, so a plate shows as the plate and a location as the
        // location. The operator sees their scan confirmed rather than rewritten.
        setText(found.licensePlateNumber ?? found.locationCode);
        advanceTo?.focus();
      } catch (err) {
        setValue(null);
        setText('');
        setError(
          err instanceof ApiError
            ? (err.problem.detail ?? err.problem.title ?? t('inventory.moveScreen.endUnknown'))
            : t('inventory.moveScreen.endUnknown'),
        );
        (which === 'from' ? fromRef : toRef).current?.focus();
      } finally {
        setResolving(null);
      }
    },
    [fromText, toText, from, to, t],
  );

  /** Resolves the item and lays out the rest of the form from what came back. */
  const resolveItem = useCallback(
    async (raw?: string) => {
      const number = (raw ?? itemText).trim();
      if (!number) {
        setItem(null);
        setItemError(null);
        return;
      }
      if (item && item.itemNumber === number.toUpperCase()) return;

      setResolving('item');
      setItemError(null);
      try {
        const found = await scanItem(number);
        setItem(found);
        setItemText(found.itemNumber);
        // The unit follows the item, as it does in the current product.
        setUomId(found.primaryUomId);
        // One serial is one physical thing, so there is nothing to decide.
        setQuantity(found.isSerialized ? 1 : '');
        setLotNumber('');
        setSerialNumber('');
      } catch (err) {
        setItem(null);
        setItemText('');
        setUomId('');
        setItemError(
          err instanceof ApiError
            ? (err.problem.title ?? t('inventory.moveScreen.itemUnknown'))
            : t('inventory.moveScreen.itemUnknown'),
        );
        itemRef.current?.focus();
      } finally {
        setResolving(null);
      }
    },
    [itemText, item, t],
  );

  // Arriving from the inquiry screen, which can hand over the item it was showing.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !search.itemNumber) return;
    prefilled.current = true;
    setItemText(search.itemNumber);
    void resolveItem(search.itemNumber);
  }, [search.itemNumber, resolveItem]);

  /** Everything except the two ends: Save leaves you standing where you were. */
  const resetLine = useCallback(() => {
    setItemText('');
    setItem(null);
    setItemError(null);
    setQuantity('');
    setUomId('');
    setLotNumber('');
    setSerialNumber('');
    setFieldErrors({});
    setSubmitError(null);
    itemRef.current?.focus();
  }, []);

  /** Reset, which legacy offers alongside Save: clears the whole form including the ends. */
  const resetAll = useCallback(() => {
    setFromText('');
    setToText('');
    setFrom(null);
    setTo(null);
    setFromError(null);
    setToError(null);
    resetLine();
    fromRef.current?.focus();
  }, [resetLine]);

  const mutation = useMutation({
    mutationFn: () =>
      postMove({
        // A plate resolves to the location it is standing in, which is what gets posted. The
        // operator never has to know where that is — the whole point of scanning the plate.
        warehouseId: from!.warehouseId,
        itemId: item!.itemId,
        fromLocationId: from!.locationId,
        toLocationId: to!.locationId,
        fromLicensePlateId: from!.licensePlateId,
        toLicensePlateId: to!.licensePlateId,
        quantity: Number(quantity),
        uomId,
        // Text, not ids: the server resolves them, and refusing the move rolls back any lot it
        // registered on the way. No stock category — each location carries its own.
        lotNumber: item!.isLotControlled ? lotNumber.trim().toUpperCase() : null,
        serialNumber: item!.isSerialized ? serialNumber.trim().toUpperCase() : null,
      }),
    onSuccess: () => {
      notifications.show({
        message: t('inventory.moveScreen.posted', {
          quantity: Number(quantity).toLocaleString(),
          item: item?.itemNumber ?? '',
          to: to?.licensePlateNumber ?? to?.locationCode ?? '',
        }),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      resetLine();
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('inventory.errors.moveFailed'))
          : t('inventory.errors.moveFailed'),
      );
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const next: Record<string, string> = {};
    if (!from) next.from = t('inventory.moveScreen.fromRequired');
    if (!to) next.to = t('inventory.moveScreen.toRequired');
    if (!item) next.item = t('inventory.moveScreen.itemRequired');
    if (quantity === '' || Number(quantity) <= 0) next.quantity = t('inventory.moveScreen.quantityRequired');
    if (!uomId) next.uom = t('inventory.moveScreen.uomRequired');
    if (item?.isLotControlled && !lotNumber.trim()) next.lot = t('inventory.moveScreen.lotRequired');
    if (item?.isSerialized && !serialNumber.trim()) next.serial = t('inventory.moveScreen.serialRequired');

    // Caught here as well as by the server, because it is the one mistake with no useful message
    // from the far end: "nothing would move" reads as a bug rather than a typo.
    if (from && to && from.locationId === to.locationId
        && (from.licensePlateId ?? null) === (to.licensePlateId ?? null)) {
      next.to = t('inventory.moveScreen.sameEnds');
    }

    setFieldErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  if (!canMove) {
    return (
      <>
        <PageHeader title={t('nav.move')} />
        <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
          {t('inventory.moveScreen.notPermitted')}
        </Alert>
      </>
    );
  }

  /** What a resolved end turned out to be, said in one line under the box. */
  function endHint(end: ScannedLocationDto | null) {
    if (!end) return undefined;
    return end.kind === 'license_plate'
      ? t('inventory.moveScreen.plateAt', {
          plate: end.licensePlateNumber ?? '',
          location: end.locationCode,
        })
      : `${end.locationCode}${end.locationName ? ` — ${end.locationName}` : ''}`;
  }

  return (
    <>
      <PageHeader title={t('nav.move')} subtitle={t('inventory.moveScreen.subtitle')} />

      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md" maw={720}>
          <TextInput
            ref={fromRef}
            label={t('inventory.moveScreen.from')}
            placeholder={t('inventory.moveScreen.endPlaceholder')}
            description={endHint(from)}
            value={fromText}
            onChange={(e) => setFromText(e.currentTarget.value)}
            onBlur={(e) => void resolveEnd('from', e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              // A scanner sends Enter. Resolve and step on rather than submitting the form.
              e.preventDefault();
              void resolveEnd('from', e.currentTarget.value, toRef.current);
            }}
            error={fieldErrors.from ?? fromError ?? undefined}
            rightSection={resolving === 'from' ? <Loader size="xs" /> : undefined}
            autoFocus
          />

          <TextInput
            ref={toRef}
            label={t('inventory.moveScreen.to')}
            placeholder={t('inventory.moveScreen.endPlaceholder')}
            description={endHint(to)}
            value={toText}
            onChange={(e) => setToText(e.currentTarget.value)}
            onBlur={(e) => void resolveEnd('to', e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              void resolveEnd('to', e.currentTarget.value, itemRef.current);
            }}
            error={fieldErrors.to ?? toError ?? undefined}
            rightSection={resolving === 'to' ? <Loader size="xs" /> : undefined}
          />

          <TextInput
            ref={itemRef}
            label={t('inventory.moveScreen.item')}
            placeholder={t('inventory.moveScreen.itemPlaceholder')}
            // Legacy shows the description under the combo, which is how somebody checks they have
            // the right item without leaving the screen.
            description={item?.description}
            value={itemText}
            onChange={(e) => setItemText(e.currentTarget.value)}
            onBlur={(e) => void resolveItem(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              void resolveItem(e.currentTarget.value).then(() => qtyRef.current?.focus());
            }}
            error={fieldErrors.item ?? itemError ?? undefined}
            rightSection={resolving === 'item' ? <Loader size="xs" /> : undefined}
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput
              ref={qtyRef}
              label={t('inventory.moveScreen.quantity')}
              value={quantity}
              onChange={(v) => setQuantity(v === '' ? '' : Number(v))}
              // One serial is one unit, so there is nothing to type.
              disabled={item?.isSerialized === true}
              description={item?.isSerialized ? t('inventory.moveScreen.serialIsOne') : undefined}
              min={0}
              decimalScale={4}
              error={fieldErrors.quantity}
            />
            <Select
              label={t('inventory.moveScreen.uom')}
              data={uomOptions}
              value={uomId || null}
              onChange={(v) => setUomId(v ?? '')}
              disabled={!item || uoms.isLoading}
              error={fieldErrors.uom}
              searchable
            />
          </SimpleGrid>

          {/* Only for the items that have them. Legacy shows the lot box always; showing it for an
              item that does not track lots invites somebody to fill it in and wonder why it was
              ignored. */}
          {item?.isLotControlled && (
            <TextInput
              label={t('inventory.moveScreen.lot')}
              value={lotNumber}
              onChange={(e) => setLotNumber(e.currentTarget.value)}
              error={fieldErrors.lot}
            />
          )}

          {item?.isSerialized && (
            <TextInput
              label={t('inventory.moveScreen.serial')}
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.currentTarget.value)}
              error={fieldErrors.serial}
            />
          )}

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group gap="sm">
            <Button type="submit" color="invetrak" loading={mutation.isPending}>
              {t('inventory.moveScreen.save')}
            </Button>
            <Button variant="default" onClick={resetAll} type="button">
              {t('inventory.moveScreen.reset')}
            </Button>
          </Group>

          <Text size="xs" c="dimmed">
            {t('inventory.moveScreen.savedHint')}
          </Text>
        </Stack>
      </form>
    </>
  );
}
